import { EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { getDownloadURL } from 'library/firebase/fb.utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectDefaultCategoryIconByID } from 'reduxStore/defaultCategories/selectors';
import { devicesActions } from 'reduxStore/devices/slice';
import { Avatar, Button, Flex, Image, Input, Typography } from 'zui/Antd';
import { SpinWhite } from 'zui/Custom';

const { Text } = Typography;

const EditDrawerHeader = ({ deviceID, name, description, defaultCategoryID }) => {
	const dispatch = useDispatch();
	const inputRef = useRef(null);
	const formColRef = useRef(null);
	const {
		token: {
			paddingLG,
			colorTextLabel,
			fontSizeHeading3,
			colorPrimary,
			fontSizeHeading5,
			colorText,
			fontWeightStrong,
			fontSizeSM,
			colorError
		}
	} = theme.useToken();

	// Redux state
	const defaultCategoryIcon = useSelector((state) => selectDefaultCategoryIconByID(state, defaultCategoryID));
	const [editable, setEditable] = useState(false);
	const [updatedName, setUpdatedName] = useState(name);
	const [errors, setErrors] = useState({ name: '' });
	const [icon, setIcon] = useState(null);

	// Get default category icon
	useEffect(() => {
		getDownloadURL(defaultCategoryIcon).then((url) => {
			setIcon(url);
		});
	}, [defaultCategoryIcon]);

	// Reset when group changes
	useEffect(() => {
		setUpdatedName(name);
		setEditable(false);
		setErrors({ name: '' });
	}, [deviceID, name]);

	// Autofocus
	useEffect(() => {
		if (editable && inputRef.current) {
			inputRef.current.focus();
		}
	}, [editable]);

	const handleFinish = useCallback(() => {
		const trimmedName = updatedName.trim();
		const newErrors = { name: '' };

		if (!trimmedName) {
			newErrors.name = 'Name is required';
		}

		setErrors(newErrors);

		if (newErrors.name) return;

		if (trimmedName !== name.trim()) {
			dispatch(devicesActions.updateDevice({ deviceID, data: { name: trimmedName } }));
		}

		setEditable(false);
	}, [updatedName, name, deviceID, dispatch]);

	// Save on outside click
	useEffect(() => {
		const handleClickOutside = (event) => {
			const clickedOutside = formColRef.current && !formColRef.current.contains(event.target);
			if (editable && clickedOutside) {
				handleFinish();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [editable, handleFinish]);

	return (
		<Flex gap={paddingLG}>
			{/* Icon */}
			<Avatar
				size={54}
				src={
					icon ? (
						<Image
							src={icon}
							width={40}
							height={44}
							preview={false}
							loading='lazy'
							style={{
								filter: 'brightness(0) invert(1)'
							}}
						/>
					) : (
						<SpinWhite size='small' />
					)
				}
				style={{ backgroundColor: '#FFBB50', alignSelf: 'start', marginTop: 6 }}
				shape='square'
			/>

			{/* Name + UID */}
			<Flex vertical flex='1' ref={formColRef}>
				{editable ? (
					<>
						<Input
							ref={inputRef}
							name='groupName'
							style={{
								fontSize: fontSizeHeading3,
								fontWeight: fontWeightStrong,
								color: colorText,
								lineHeight: '32px',
								padding: 0
							}}
							value={updatedName}
							onPressEnter={handleFinish}
							onChange={(e) => setUpdatedName(e.target.value)}
							placeholder='Group name'
						/>
						{errors.name && (
							<Text
								style={{
									color: colorError,
									fontSize: fontSizeSM,
									fontWeight: fontWeightStrong
								}}
							>
								{errors.name}
							</Text>
						)}
					</>
				) : (
					<Text
						style={{
							fontSize: fontSizeHeading3,
							fontWeight: fontWeightStrong,
							lineHeight: '32px',
							color: colorText
						}}
					>
						{updatedName}
					</Text>
				)}

				<Text
					style={{
						fontSize: fontSizeHeading5,
						fontWeight: fontWeightStrong,
						color: colorTextLabel,
						lineHeight: '24px',
						marginBottom: 0
					}}
				>
					{description}
				</Text>
			</Flex>

			{/* Edit Button */}
			<Button
				onClick={() => setEditable((prev) => !prev)}
				type='text'
				icon={<EditOutlined style={{ color: editable ? colorPrimary : 'inherit', fontSize: 18 }} />}
				style={{ alignSelf: 'start' }}
			/>
		</Flex>
	);
};

export default EditDrawerHeader;
